import React, { useState, useEffect } from 'react'
import { Plus, Edit2, Trash2, Power, PowerOff, RefreshCw, X, Check, AlertTriangle } from 'lucide-react'
import { supabase } from '../../../lib/supabase'

interface Machine {
  id: number
  name: string
  status: string
  hal: string
  location?: string
  created_at?: string
}

interface MachineFormData {
  name: string
  hal: string
  location: string
  status: string
}

const emptyFormData: MachineFormData = {
  name: '',
  hal: '',
  location: '',
  status: 'active'
}

export const MachineManagerModule: React.FC = () => {
  const [machines, setMachines] = useState<Machine[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null)
  const [formData, setFormData] = useState<MachineFormData>(emptyFormData)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const [availableHalls, setAvailableHalls] = useState<string[]>([])

  useEffect(() => {
    loadMachines()
  }, [showInactive])

  const loadMachines = async () => {
    try {
      setIsLoading(true)
      setError(null)

      let query = supabase
        .from('machines')
        .select('*')
        .order('hal, name')

      if (!showInactive) {
        query = query.neq('status', 'inactive')
      }

      const { data, error: fetchError } = await query

      if (fetchError) {
        console.error('Error loading machines:', fetchError)
        setError('Fout bij het laden van machines')
        return
      }

      setMachines(data || [])

      // Extract unique halls
      const halls = [...new Set((data || []).map(m => m.hal).filter(Boolean))].sort()
      setAvailableHalls(halls as string[])

    } catch (err) {
      console.error('Error:', err)
      setError('Onverwachte fout opgetreden')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAddMachine = async () => {
    if (!formData.name.trim() || !formData.hal.trim()) {
      setError('Naam en Hal zijn verplicht')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const { error: insertError } = await supabase
        .from('machines')
        .insert([{
          name: formData.name.trim(),
          hal: formData.hal.trim(),
          location: formData.location.trim() || null,
          status: formData.status || 'active'
        }])

      if (insertError) {
        console.error('Error adding machine:', insertError)
        setError('Fout bij het toevoegen van machine: ' + insertError.message)
        return
      }

      setShowAddModal(false)
      setFormData(emptyFormData)
      await loadMachines()
    } catch (err) {
      console.error('Error:', err)
      setError('Onverwachte fout opgetreden')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleEditMachine = async () => {
    if (!selectedMachine || !formData.name.trim() || !formData.hal.trim()) {
      setError('Naam en Hal zijn verplicht')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const { error: updateError } = await supabase
        .from('machines')
        .update({
          name: formData.name.trim(),
          hal: formData.hal.trim(),
          location: formData.location.trim() || null,
          status: formData.status
        })
        .eq('id', selectedMachine.id)

      if (updateError) {
        console.error('Error updating machine:', updateError)
        setError('Fout bij het bijwerken van machine: ' + updateError.message)
        return
      }

      setShowEditModal(false)
      setSelectedMachine(null)
      setFormData(emptyFormData)
      await loadMachines()
    } catch (err) {
      console.error('Error:', err)
      setError('Onverwachte fout opgetreden')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSoftDelete = async () => {
    if (!selectedMachine) return

    setIsSubmitting(true)
    setError(null)

    try {
      const { error: updateError } = await supabase
        .from('machines')
        .update({ status: 'inactive' })
        .eq('id', selectedMachine.id)

      if (updateError) {
        console.error('Error deactivating machine:', updateError)
        setError('Fout bij het deactiveren van machine: ' + updateError.message)
        return
      }

      setShowDeleteModal(false)
      setSelectedMachine(null)
      await loadMachines()
    } catch (err) {
      console.error('Error:', err)
      setError('Onverwachte fout opgetreden')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleHardDelete = async () => {
    if (!selectedMachine) return

    setIsSubmitting(true)
    setError(null)

    try {
      const { error: deleteError } = await supabase
        .from('machines')
        .delete()
        .eq('id', selectedMachine.id)

      if (deleteError) {
        console.error('Error deleting machine:', deleteError)
        setError('Fout bij het verwijderen van machine: ' + deleteError.message)
        return
      }

      setShowDeleteModal(false)
      setSelectedMachine(null)
      await loadMachines()
    } catch (err) {
      console.error('Error:', err)
      setError('Onverwachte fout opgetreden')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleReactivate = async (machine: Machine) => {
    setIsSubmitting(true)
    setError(null)

    try {
      const { error: updateError } = await supabase
        .from('machines')
        .update({ status: 'active' })
        .eq('id', machine.id)

      if (updateError) {
        console.error('Error reactivating machine:', updateError)
        setError('Fout bij het heractiveren van machine')
        return
      }

      await loadMachines()
    } catch (err) {
      console.error('Error:', err)
      setError('Onverwachte fout opgetreden')
    } finally {
      setIsSubmitting(false)
    }
  }

  const openEditModal = (machine: Machine) => {
    setSelectedMachine(machine)
    setFormData({
      name: machine.name,
      hal: machine.hal || '',
      location: machine.location || '',
      status: machine.status || 'active'
    })
    setShowEditModal(true)
  }

  const openDeleteModal = (machine: Machine) => {
    setSelectedMachine(machine)
    setShowDeleteModal(true)
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Actief</span>
      case 'inactive':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">Inactief</span>
      case 'maintenance':
        return <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Onderhoud</span>
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">{status || 'Onbekend'}</span>
    }
  }

  return (
    <div className="module-content">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Machine Beheer</h2>
        <div className="flex gap-2">
          <button
            onClick={loadMachines}
            disabled={isLoading}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            Ververs
          </button>
          <button
            onClick={() => {
              setFormData(emptyFormData)
              setShowAddModal(true)
            }}
            className="bg-red-800 text-white px-3 py-1 rounded text-sm hover:bg-red-900 flex items-center gap-1"
          >
            <Plus size={16} />
            Machine Toevoegen
          </button>
        </div>
      </div>

      {/* Show inactive toggle */}
      <div className="mb-4">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="form-checkbox text-red-600 rounded"
          />
          <span className="text-gray-700 text-sm">Toon ook inactieve machines</span>
        </label>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
            <X size={18} />
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="text-center text-gray-500 py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-800"></div>
          <p className="mt-2">Machines laden...</p>
        </div>
      ) : machines.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <p>Geen machines gevonden.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Naam</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hal</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Locatie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {machines.map((machine) => (
                <tr key={machine.id} className={machine.status === 'inactive' ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                  <td className="px-4 py-3 text-sm text-gray-900">{machine.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{machine.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{machine.hal || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{machine.location || '-'}</td>
                  <td className="px-4 py-3 text-sm">{getStatusBadge(machine.status)}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(machine)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Bewerken"
                      >
                        <Edit2 size={16} />
                      </button>
                      {machine.status === 'inactive' ? (
                        <button
                          onClick={() => handleReactivate(machine)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Heractiveren"
                          disabled={isSubmitting}
                        >
                          <Power size={16} />
                        </button>
                      ) : (
                        <button
                          onClick={() => openDeleteModal(machine)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Verwijderen"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-sm text-gray-500">
        Totaal: {machines.length} machine(s) {showInactive && `(inclusief inactieve)`}
      </div>

      {/* Add Machine Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Nieuwe Machine Toevoegen</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Naam *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="Machine naam"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hal *</label>
                <input
                  type="text"
                  value={formData.hal}
                  onChange={(e) => setFormData({ ...formData, hal: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="Hal naam (bijv. Hal A)"
                  list="halls-list"
                />
                <datalist id="halls-list">
                  {availableHalls.map(hall => (
                    <option key={hall} value={hall} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Locatie</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="Optionele locatie beschrijving"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                >
                  <option value="active">Actief</option>
                  <option value="maintenance">Onderhoud</option>
                  <option value="inactive">Inactief</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuleren
              </button>
              <button
                onClick={handleAddMachine}
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
                Toevoegen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Machine Modal */}
      {showEditModal && selectedMachine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Machine Bewerken</h3>
              <button onClick={() => setShowEditModal(false)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Naam *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hal *</label>
                <input
                  type="text"
                  value={formData.hal}
                  onChange={(e) => setFormData({ ...formData, hal: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  list="halls-list-edit"
                />
                <datalist id="halls-list-edit">
                  {availableHalls.map(hall => (
                    <option key={hall} value={hall} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Locatie</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                >
                  <option value="active">Actief</option>
                  <option value="maintenance">Onderhoud</option>
                  <option value="inactive">Inactief</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuleren
              </button>
              <button
                onClick={handleEditMachine}
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-800 text-white rounded-lg hover:bg-red-900 disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
                Opslaan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedMachine && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Machine Verwijderen</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Wat wil je doen met machine <strong>{selectedMachine.name}</strong>?
            </p>

            <div className="space-y-3 mb-6">
              <div className="p-3 border border-gray-200 rounded-lg">
                <h4 className="font-medium text-gray-800 flex items-center gap-2">
                  <PowerOff size={16} className="text-yellow-600" />
                  Soft Delete (Aanbevolen)
                </h4>
                <p className="text-sm text-gray-600 mt-1">
                  Zet de machine op inactief. De machine verdwijnt uit dagelijkse lijsten maar de historie blijft bewaard.
                </p>
                <button
                  onClick={handleSoftDelete}
                  disabled={isSubmitting}
                  className="mt-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 text-sm"
                >
                  Deactiveren
                </button>
              </div>

              <div className="p-3 border border-red-200 rounded-lg bg-red-50">
                <h4 className="font-medium text-red-800 flex items-center gap-2">
                  <Trash2 size={16} className="text-red-600" />
                  Hard Delete (Permanent)
                </h4>
                <p className="text-sm text-red-600 mt-1">
                  Verwijder de machine definitief uit de database. Dit kan niet ongedaan worden gemaakt!
                </p>
                <button
                  onClick={handleHardDelete}
                  disabled={isSubmitting}
                  className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm"
                >
                  Permanent Verwijderen
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false)
                  setSelectedMachine(null)
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
